/**
 * Tooltip initialization service for preloading tooltips into Dexie database
 * This replaces the inefficient file loading on every tooltip request
 */

import { marked } from 'marked';
import { InspectDatabase } from './database.structure';
import { MdHelpers } from '../helpers/mdHelpers';
import { TooltipModel } from './types';

export class TooltipInitializer {
  private db: InspectDatabase;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  constructor(db: InspectDatabase) {
    this.db = db;
  }

  /**
   * Initialize tooltips by loading them once into Dexie database
   * Subsequent calls return the cached promise
   */
  async initializeTooltips(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    try {
      console.log('🔄 Initializing tooltips cache...');
      const startTime = performance.now();

      // Check if tooltips are already cached
      // const existingCount = await this.db.tooltips.count();
      // if (existingCount > 0) {
      //     console.log(`✅ Tooltips already cached (${existingCount} tooltips)`);
      //     this.isInitialized = true;
      //     return;
      // }

      // Load optimized markdown file (with image URLs instead of base64)
      const fileContent = await this.loadOptimizedTooltipsFile();

      // Parse markdown into tooltip models
      const tooltips = await this.parseTooltipsFromMarkdown(fileContent);

      // Batch insert into Dexie for performance
      await this.db.transaction('rw', this.db.tooltips, async () => {
        // Builds before #9710 stored keys only lowercased; drop those copies.
        // Editor saves are already normalised, so they are never matched here.
        // Also drop unedited rows for sections removed from the file (#11465),
        // or the tooltip export writes them back.
        const fileKeys = new Set(tooltips.map((tooltip) => tooltip.key));
        const rows = await this.db.tooltips.toArray();
        await this.db.tooltips.bulkDelete(
          rows
            .filter(
              (row) =>
                row.key !== MdHelpers.normaliseTooltipKeyComponent(row.key) ||
                (!row.isModified && !fileKeys.has(row.key)),
            )
            .map((row) => row.key),
        );
        await this.db.tooltips.bulkPut(tooltips);
      });

      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);

      console.log(
        `✅ Tooltips cache initialized: ${tooltips.length} tooltips in ${duration}ms`,
      );
      this.isInitialized = true;
    } catch (error) {
      console.error('❌ Failed to initialize tooltips cache:', error);
      this.initializationPromise = null;
      throw error;
    }
  }

  private async loadOptimizedTooltipsFile(): Promise<string> {
    try {
      // Try to load optimized version first (with extracted images)
      const optimized = await import('./local-files/tooltips-optimized.md?raw');
      return optimized.default;
    } catch {
      // Fallback to original file if optimized version doesn't exist
      console.warn(
        '⚠️ Optimized tooltips file not found, using original (this will be slower)',
      );
      const original = await import('./local-files/tooltips.md?raw');
      return original.default;
    }
  }

  private async parseTooltipsFromMarkdown(
    fileContent: string,
  ): Promise<TooltipModel[]> {
    const groupedTokens = MdHelpers.getGroupedTokensByHeaders(fileContent);
    const keyRegex = /{#[^}]+}/g;
    const keyGroupedTokens = MdHelpers.groupHeadersByKeys(
      groupedTokens,
      keyRegex,
    );

    return keyGroupedTokens.map((group) => ({
      key: MdHelpers.normaliseTooltipKeyComponent(group.key),
      heading: group.heading,
      markdownBody: group.childTokens.map((token) => token.raw).join(''),
      htmlBody: marked.parser(group.childTokens),
      isModified: false,
    }));
  }

  /**
   * Check if tooltips have been initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get tooltip count for diagnostics
   */
  async getTooltipCount(): Promise<number> {
    return await this.db.tooltips.count();
  }

  /**
   * Clear the cache (useful for development/testing)
   */
  async clearCache(): Promise<void> {
    await this.db.tooltips.clear();
    this.isInitialized = false;
    this.initializationPromise = null;
    console.log('🗑️ Tooltips cache cleared');
  }
}

// Singleton instance for the app
let tooltipInitializer: TooltipInitializer | null = null;

export function getTooltipInitializer(db: InspectDatabase): TooltipInitializer {
  if (!tooltipInitializer) {
    tooltipInitializer = new TooltipInitializer(db);
  }
  return tooltipInitializer;
}
